import type { EnvMatrix, EnvMatrixRow, EnvMatrixService } from "@/lib/api";
import { SOURCE_LABEL, rulesFor } from "@/lib/envMatrix";
import { copyText } from "@/lib/clipboard";
import { CompareMark, RuleMark, columnLabel, type RowRef } from "./MatrixGrid";
import { TINT } from "./tints";
import { useRevealNow } from "./reveal";

/**
 * One key across the table's services, side by side with the real values —
 * secrets too, for an admin, since opening this is asking to see them — so
 * they can be compared by eye. Each with where it's set and a copy button;
 * then whether they match, the rules that check them and any notices.
 */
export function KeyDetails({
  m,
  at,
  serviceIds,
  onNotify,
}: {
  m: EnvMatrix;
  at: RowRef;
  serviceIds: string[];
  onNotify: (message: string) => void;
}) {
  const row = m.rows.find((r) => r.key === at.key && r.kind === at.kind);
  if (!row) return null;
  const services = serviceIds.map((id) => m.services.find((s) => s.id === id)!);
  const rules = rulesFor(m, row.key, serviceIds);
  const notices = m.issues.filter(
    (i) => i.kind === "notice" && i.cells.some((c) => c.key === row.key && serviceIds.includes(c.serviceId)),
  );
  const anySecret = services.some((s) => row.cells[s.id]?.secret);

  return (
    <div className="space-y-4 text-sm">
      <CompareMark row={row} services={services} withText />

      <div className={`grid gap-3 ${services.length > 1 ? "md:grid-cols-2" : ""} ${services.length > 2 ? "xl:grid-cols-3" : ""}`}>
        {services.map((s) => (
          <ServiceValue key={s.id} row={row} svc={s} onNotify={onNotify} />
        ))}
      </div>

      {rules.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">Rules</h3>
          <ul className="space-y-1">
            {rules.map((r, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="mt-0.5">
                  <RuleMark ok={r.ok} />
                </span>
                {r.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {notices.map((n) => (
        <div key={n.id} className={`rounded-token-md border p-2.5 text-xs ${TINT.warningBorder} ${TINT.warning}`}>
          <p className="font-semibold text-text-heading">{n.title}</p>
          {n.detail && <p className="mt-0.5 text-text">{n.detail}</p>}
          {n.fix && <p className="mt-0.5 text-text-secondary">{n.fix}</p>}
        </div>
      ))}

      {anySecret && (
        <p className="text-xs text-text-muted">
          Secret values are shown to admins only, and each one shown or copied is noted in the audit log.
        </p>
      )}
    </div>
  );
}

function ServiceValue({
  row,
  svc,
  onNotify,
}: {
  row: EnvMatrixRow;
  svc: EnvMatrixService;
  onNotify: (message: string) => void;
}) {
  const cell = row.cells[svc.id];
  const isSecret = !!(cell?.set && cell.secret);
  const reveal = useRevealNow(
    isSecret && row.kind === "var"
      ? { serviceId: svc.id, key: row.key, fingerprint: cell!.fingerprint, revealable: cell!.revealable }
      : null,
  );
  const value = cell?.set ? (isSecret ? (reveal.status === "shown" ? reveal.value : null) : (cell.value ?? "")) : null;

  async function copy(text: string) {
    try {
      await copyText(text);
      onNotify(`Copied ${row.key} from ${svc.name}.`);
    } catch (e) {
      onNotify(`Couldn't copy: ${(e as Error).message}`);
    }
  }

  let body: React.ReactNode;
  if (value !== null) {
    body = <code className="break-all font-mono text-xs text-text-heading">{value || "(empty)"}</code>;
  } else if (isSecret && row.kind === "file") {
    body = <span className="text-text-muted">A secret file. Its contents aren't shown.</span>;
  } else if (isSecret && reveal.status === "unavailable") {
    body = (
      <span className="text-text-secondary">
        <i className="fa-solid fa-lock mr-1.5" />
        {reveal.reason}
      </span>
    );
  } else if (isSecret) {
    body = (
      <span className="text-text-muted">
        <i className="fa-solid fa-circle-notch fa-spin mr-1.5" />
        Revealing
      </span>
    );
  } else if (cell?.flag === "missing") {
    body = <span className="text-warning">Not set. A rule expects it here.</span>;
  } else if (cell) {
    body = (
      <span className="text-text-muted">
        Not set. render.yaml declares it{cell.line ? ` at line ${cell.line}` : ""}.
      </span>
    );
  } else {
    body = <span className="text-text-muted">Not set on this service.</span>;
  }

  return (
    <section className="flex min-w-0 flex-col gap-2 rounded-token-md border border-border bg-bg-card p-3">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-text-heading">{columnLabel(svc)}</p>
          <p className="truncate font-mono text-[11px] text-text-muted">{svc.name}</p>
        </div>
        {value !== null && (
          <button
            type="button"
            onClick={() => copy(value)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-token-sm border border-border px-2 py-1 text-xs font-medium text-text-heading transition-colors hover:bg-bg-card-hover"
          >
            <i className="fa-regular fa-copy" />
            Copy
          </button>
        )}
      </header>

      <div className="min-h-[2.25rem] rounded-token-sm bg-bg-secondary px-2.5 py-2">{body}</div>

      {cell && (
        <p className="text-xs text-text-muted">
          {cell.group ? `From the env group ${cell.group}` : SOURCE_LABEL[cell.source]}
          {cell.fileUrl && (
            <a href={cell.fileUrl} target="_blank" rel="noopener noreferrer" className="ml-1 text-accent hover:underline">
              render.yaml line {cell.line} <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
            </a>
          )}
          {cell.overridesGroup && <span className="block">Overrides the value from the env group {cell.overridesGroup}.</span>}
        </p>
      )}

      {svc.dashboardUrl && (
        <a
          href={svc.dashboardUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-auto inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
        >
          Open in Render <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
        </a>
      )}
    </section>
  );
}
